import classnames from 'classnames';
import { isEqual, isUndefined, noop } from 'lodash/fp';
import React, { useCallback, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '../../../../../../hooks/react-redux';
import { useSingleHiddenDocumentFields, useSingleHiddenFields } from '../../../../../../hooks/useHiddenSingleFields';
import { SingleFieldPreview } from '../../../../../admin/dataset-builder/preview/formPreview/SingleFieldPreview';
import { DatasetType, DocumentSpecificHiddenFields, GroupDatasetField, HiddenFields, OpenFieldType } from '../../../../../datasets/store/types';
import { PlusButton } from '../../../../../shared/button/PlusButton';
import { Toggle } from '../../../../../shared/toggle';
import { OverflowTooltip } from '../../../../../shared/tooltip';
import { addDocumentReportField, DocumentReportField, getReportDatasetDefinitions, getReportFields, getSelectedDocumentNameIds, removeDocumentReportField } from '../../../store';
import styles from '../../ReportDefinitions.module.scss';

const { lightGrey, green } = styles;

interface MultiToggleFieldProps {
    field: GroupDatasetField;
    groupIndex: number;
    sectionId: string;
    datasetId: number;
    hiddenFields: HiddenFields;
    datasetHidden?: boolean;
    documentSpecificHiddenFields: DocumentSpecificHiddenFields;
    parentFieldId: string;
}

export const MultiToggleField: React.FC<MultiToggleFieldProps> = ({
    field,
    groupIndex,
    sectionId,
    datasetId,
    hiddenFields,
    datasetHidden,
    documentSpecificHiddenFields,
    parentFieldId
}) => {
    const dispatch = useAppDispatch();
    const reportDocumentNameIds = useAppSelector(getSelectedDocumentNameIds);
    const datasetDefinitions = useAppSelector(getReportDatasetDefinitions);
    const reportFields = useAppSelector(getReportFields);

    const { children, label, id } = field;

    const getOptionWrapperStyle = useCallback((index: number) => {
        const totalEntries = children.length;
        const numberOfLastRowEntries = totalEntries % 3 || 3;
        const lastRowEntries = Array(numberOfLastRowEntries).fill(totalEntries - numberOfLastRowEntries).map((val, i) => val + i);
        const isLastRow = lastRowEntries.includes(index);
        const isFirstColumn = ((index + 1) % 3) === 1;
        if (!isLastRow) {
            return !isFirstColumn ? { width: 'calc((100% / 3) - 24px)', paddingLeft: '15px', borderLeft: `1px solid ${lightGrey}`, borderBottom: `1px solid ${lightGrey}` } : { width: 'calc((100% / 3) - 10px)', borderBottom: `1px solid ${lightGrey}` };
        }
        return !isFirstColumn ? { width: 'calc((100% / 3) - 24px)', paddingLeft: '15px', borderLeft: `1px solid ${lightGrey}` } : { width: 'calc((100% / 3) - 10px)' };
    }, [children]);

    const currentHiddenDatasetFields = useMemo(() => hiddenFields[datasetId] || [], [datasetId, hiddenFields]);

    const hiddenSingleFields = useSingleHiddenFields(currentHiddenDatasetFields);

    const sectionIsHidden = useMemo(() => !!currentHiddenDatasetFields.filter(({ type }) => type === OpenFieldType.SECTION).find(field => field.sectionId === sectionId), [currentHiddenDatasetFields, sectionId]);

    const fieldHidden = useMemo(() => sectionIsHidden || datasetHidden || !!hiddenSingleFields.find(({ fieldId }) => fieldId === id), [hiddenSingleFields, id, sectionIsHidden, datasetHidden]);

    const documentHiddenFields = useMemo(() => documentSpecificHiddenFields[datasetId] || [], [datasetId, documentSpecificHiddenFields]);
    const documentHiddenSingleFields = useSingleHiddenDocumentFields(documentHiddenFields);
    const documentsHiddenForSection = useMemo(() => documentHiddenFields.filter(({ type }) => type === OpenFieldType.SECTION).find(hiddenField => hiddenField.sectionId === sectionId)?.documentNameIds || [], [documentHiddenFields, sectionId]);
    const documentsHiddenForField = useMemo(() => documentHiddenSingleFields.find(({ fieldId }) => fieldId === id)?.documentNameIds || [], [documentHiddenSingleFields, id]);
    const fieldHiddenForAllDatasetDocuments = useMemo(() => documentsHiddenForField.length > 0 && reportDocumentNameIds.every(documentNameId => documentsHiddenForField.includes(parseInt(documentNameId))), [reportDocumentNameIds, documentsHiddenForField]);
    const sectionHiddenForAllDatasetDocuments = useMemo(() => documentsHiddenForSection.length > 0 && reportDocumentNameIds.every(documentNameId => documentsHiddenForSection.includes(parseInt(documentNameId))), [reportDocumentNameIds, documentsHiddenForSection]);

    const datasetDefinition = useMemo(() => datasetDefinitions.find(definition => definition.datasetId === datasetId), [datasetDefinitions, datasetId]);
    const datasetTitle = useMemo(() => datasetDefinition?.datasetTitle || '', [datasetDefinition]);

    const documentReportField: DocumentReportField = useMemo(() => ({ datasetId, datasetTitle, datasetType: DatasetType.FORM, fieldName: datasetTitle, id: id!, label: label || '', parentFieldId, description: '', customLabel: '', includeDatasetTitle: datasetTitle !== label }), [id, label, parentFieldId, datasetId, datasetTitle]);
    const isIncludedReportField = useMemo(() => !isUndefined(reportFields.find(reportField => isEqual(documentReportField, reportField))), [reportFields, documentReportField]);
    const addReportField = useCallback(() => dispatch(addDocumentReportField(documentReportField)), [dispatch, documentReportField]);
    const removeReportField = useCallback(() => dispatch(removeDocumentReportField(id!)), [dispatch, id]);
    const toggleReportField = useCallback((checked: boolean) => checked ? addReportField() : removeReportField(), [removeReportField, addReportField]);

    if (fieldHidden || fieldHiddenForAllDatasetDocuments || sectionHiddenForAllDatasetDocuments) {
        return null;
    }

    return (
        <div className={styles.multiToggleWrapper}>
            <div className={styles.multiToggleGroupLabelWrapper}>
                <div className={classnames(styles.multiToggleGroupLabel, { [styles.emptyFieldLabel]: !label })}>
                    <OverflowTooltip overlayText={label || 'Label...'} />
                </div>
                <Toggle onChange={toggleReportField} checked={isIncludedReportField} onColour={green} />
            </div>
            <div className={styles.multiToggleHeaderWrapper}>
                <div className={styles.selectAllWrapper}>
                    <div className={styles.selectAllLabel}>Select All</div>
                    <Toggle onChange={noop} checked={false} disabled />
                </div>
                <div className={styles.addOptionWrapper}>
                    <div className={styles.addOptionLabel}>Add another option</div>
                    <div className={styles.addOptionButton}><PlusButton onClick={noop} fontSize={16} disabled /></div>
                </div>
            </div>
            <div className={styles.multiToggleOptionsWrapper} data-testid={`form-multi-toggle-${groupIndex}-wrapper`}>
                {children.map((field, index) => {
                    const singleIndex = `${groupIndex}${index}`;
                    const optionWrapperStyle = getOptionWrapperStyle(index);
                    return (
                        <div className={styles.toggleOptionWrapper} key={field.id} style={optionWrapperStyle}>
                            <SingleFieldPreview
                                field={field}
                                singleIndex={singleIndex}
                                index={index}
                                groupIndex={groupIndex}
                                isMultiToggle
                            />
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
